import { useQuery } from '@apollo/client';
import { navigate } from 'gatsby';
import { all, find, forEach, forEachObjIndexed, includes, is, keys, map } from 'ramda';
import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText, { IBasicTextProps } from '@ui/atoms/BasicText';
import { DashboardCardLayout } from '@ui/atoms/DashboardCardLayout';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import ProfileDropdown from '@ui/organisms/ProfileDropdown';
import TopicCardSlider from '@ui/organisms/TopicCardSlider';

import spaced from '@ui/styled/Spaced';

import { useMemberValidation } from '@hooks/useMemberValidation';
import { useServiceEligibility } from '@hooks/useServiceEligibility';

import { getCompletedHrtIntakes } from '@helpers/Intakes/intakes';

import { doesNotExist } from '@utils/general';
import { getStateName } from '@utils/intake/stateRequirements';
import { isBrowser } from '@utils/isBrowser';
import { getAllStateServiceAvailability } from '@utils/service-availability';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';
import { IAccountInfoQueryData } from '@folx-types/member';
import { IAuthenticatedMemberWithCompletedIntakes } from '@folx-types/member';

import accountInfo from '@graphql/queries/AccountInfo.query.gql';
import AUTHENTICATED_MEMBER_WITH_INTAKES from '@graphql/queries/authenticatedMemberWithIntakes.gql';

import theme from '@theme/FolxTheme';

import { GetCareFlow, ITopic, ITopicCard, dataConstants as unfilteredTopics } from './GetCare.data';

export interface IGetCarePageContent {
	heading: string;
	description: string;
	genderAffirmingCareLabel: string;
	sexualAndReproductiveHealthLabel: string;
	wellnessAndPreventativeCareLabel: string;
	feelingIllLabel: string;
	mentalHealthLabel: string;
	fertilityAndFamilyPlanningLabel: string;
	disclaimerFineprint: string;
	inactiveTooltip: string;
	tooltips: [
		{
			topicId: string;
			text: string;
		}
	];
	stateTooltips: [
		{
			stateId: string;
			text: string;
		}
	];
	stateTopicTooltips: [
		{
			stateId: string;
			topicId: string;
			text: string;
		}
	];
}

interface IGetCareProps extends IPageProps {
	pageContent: IGetCarePageContent;
}

const SpacedText = spaced<IBasicTextProps>(BasicText);

const GetCareCardLayout = styled(DashboardCardLayout)`
	max-width: 100%;
	overflow-x: hidden;
	padding: 0 ${theme.spacing.md};
	@media ${theme.responsive.breakpoints.laptop} {
		overflow-x: visible;
		padding: 0;
	}
`;

export const DescriptionText = styled(BasicText)`
	font-size: ${theme.fontSizes.body.smmd};
`;

const GetCare: FC<IGetCareProps> = ({ pageContent }) => {
	const {
		heading,
		description,
		genderAffirmingCareLabel,
		sexualAndReproductiveHealthLabel,
		wellnessAndPreventativeCareLabel,
		feelingIllLabel,
		mentalHealthLabel,
		fertilityAndFamilyPlanningLabel,
		disclaimerFineprint,
		inactiveTooltip,
		tooltips,
		stateTooltips,
		stateTopicTooltips,
	} = pageContent;

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loggedIn } = useAuth();

	useEffect(() => {
		if (isBrowser) {
			window.localStorage.removeItem('vhc_topic');
			window.localStorage.removeItem('mh_topic');
		}
	}, []);

	const { data: accountData, loading: accountLoading } = useQuery<IAccountInfoQueryData>(
		accountInfo,
		{
			fetchPolicy: 'no-cache',
		}
	);
	const [isStateServiceLoading, setStateServiceLoading] = React.useState(true);

	const {
		loaded: memberValidationLoaded,
		protectedNavigate,
		memberMetadata,
	} = useMemberValidation();

	const { eligible, loading: elgibilityLoading } = useServiceEligibility(loggedIn);

	const getCareTopics: () => Record<string, ITopic[]> | null = () => {
		if (elgibilityLoading) {
			return null;
		}

		const eligibleTopics = eligible
			? unfilteredTopics
			: {
					...unfilteredTopics,
					genderAffirmingCareTopics: map((topic) => {
						if (includes(topic.path, ['hrt_e_sub', 'hrt_t_sub', 'gaht_consult'])) {
							topic.ineligible = true;
							topic.isActive = false;
						}
						return topic;
					}, unfilteredTopics.genderAffirmingCareTopics),
			  };

		return eligibleTopics;
	};

	const { loading: intakesLoading, data: activeData } =
		useQuery<IAuthenticatedMemberWithCompletedIntakes>(AUTHENTICATED_MEMBER_WITH_INTAKES, {
			client: graphQLv2Client,
			fetchPolicy: 'no-cache',
		});

	const onTopicClick =
		({ flow, id, ...topic }: ITopicCard) =>
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();

			if (memberMetadata?.membership?.isMembershipInactive) {
				return navigate(`../reactivate_membership`);
			}
			const appFlow = `/app/flows/${flow}`;
			if (isBrowser) {
				if (flow === 'vhc_sub') {
					if (id == 2) {
						const gahtIntake = getCompletedHrtIntakes(activeData?.authenticatedMember);
						if (gahtIntake) return protectedNavigate(`/app/flows/followup/`);
					}
					window.localStorage.setItem('vhc_topic', `${id}`);
				}
				if (flow === 'mh_sub') {
					if (topic?.mhId) {
						window.localStorage.setItem('mh_topic', topic.mhId || '');
					}
				}
			}
			return protectedNavigate(appFlow);
		};

	useDeepCompareEffect(() => {
		if (doesNotExist(accountData?.currentMember) || doesNotExist(getCareTopics())) {
			return;
		}

		getAllStateServiceAvailability()
			.then((res) => {
				const currentState = accountData?.currentMember.stateOfResidence;
				if (doesNotExist(currentState)) {
					return;
				}

				const stateAvailabilityFlows = keys(res[currentState]);

				forEachObjIndexed((category) => {
					forEach((topic: ITopic) => {
						const flows = is(Array, topic.flow) ? topic.flow : [topic.flow];
						const isFlowActive = all((flow) => includes(flow, stateAvailabilityFlows), flows);
						const isTopicActive = !includes(currentState, topic?.unavailableStates ?? []);
						const isActive = isFlowActive && isTopicActive;
						return (topic.isActive = isActive);
					}, category);
				}, getCareTopics());
			})
			.finally(() => {
				setStateServiceLoading(false);
			});
	}, [accountData?.currentMember, getCareTopics()]);

	const mapCategoryWithHeader = useMemo<Record<keyof typeof getCareTopics, string>>(
		() => ({
			genderAffirmingCareTopics: genderAffirmingCareLabel,
			sexualAndReproductiveHealthTopics: sexualAndReproductiveHealthLabel,
			wellnessAndPreventativeCareTopics: wellnessAndPreventativeCareLabel,
			feelingIllTopics: feelingIllLabel,
			mentalHealthTopics: mentalHealthLabel,
			fertilityAndFamilyBuilding: fertilityAndFamilyPlanningLabel,
		}),
		[
			genderAffirmingCareLabel,
			sexualAndReproductiveHealthLabel,
			wellnessAndPreventativeCareLabel,
			feelingIllLabel,
			mentalHealthLabel,
			fertilityAndFamilyPlanningLabel,
		]
	);

	return (
		<GetCareCardLayout>
			<ProfileDropdown />
			<HeadingText>{heading}</HeadingText>
			<DescriptionText>{description}</DescriptionText>
			<LoadingWrapper
				loading={
					isStateServiceLoading ||
					accountLoading ||
					intakesLoading ||
					elgibilityLoading ||
					!memberValidationLoaded
				}
				skeleton={
					<FlexColumn gap="sm">
						<Skeleton fullWidth height="md" />
						<Skeleton structure="row" fullWidth repeated={2} width="xxxxxl" height="xxxl" />
					</FlexColumn>
				}>
				<>
					{accountData &&
						map(
							(category) => (
								<TopicCardSlider
									onClick={onTopicClick}
									category={mapCategoryWithHeader[category]}
									inactiveTooltip={inactiveTooltip}
									state={getStateName(accountData.currentMember.stateOfResidence)}
									topics={getCareTopics()?.[category]}
									tooltips={tooltips}
									stateTooltips={stateTooltips}
									stateTopicTooltips={stateTopicTooltips}
									key={category}
								/>
							),
							keys(getCareTopics())
						)}
				</>
			</LoadingWrapper>
			<SpacedText variant="fineprint" marginTop="smmd">
				{disclaimerFineprint}
			</SpacedText>
		</GetCareCardLayout>
	);
};

export default GetCare;
